import { useLayoutEffect, useState } from 'react';
import { findLastIndex, findIndex } from 'lodash/array';

const OFFSET = 12;

const emptyOverflown = { underflow: [], overflow: [] };

function getOverflownElements(el) {
  if (el) {
    const children = Array.from(el.children);
    const listRect = el.getBoundingClientRect();
    const visibleTop = listRect.top + el.clientTop;
    const visibleBottom = visibleTop + el.clientHeight;
    const underflowIdx = findLastIndex(children, c => c.getBoundingClientRect().bottom <= visibleTop + OFFSET);
    const overflowIdx = findIndex(children, c => c.getBoundingClientRect().top >= visibleBottom - OFFSET);

    return {
      underflow: children.slice(0, underflowIdx + 1),
      overflow: overflowIdx > -1 ? children.slice(overflowIdx, children.length) : [],
    };
  } else {
    return emptyOverflown;
  }
}

export const useOverflownChildren = (ref, tick) => {
  const [overflown, setOverflown] = useState(emptyOverflown);

  useLayoutEffect(() => {
    const element = ref.current;

    if (!element) {
      setOverflown(emptyOverflown);
      return undefined;
    }

    let requestRunning = null;

    const measure = () => {
      if (requestRunning === null) {
        requestRunning = window.requestAnimationFrame(() => {
          setOverflown(getOverflownElements(element));
          requestRunning = null;
        });
      }
    };

    measure();
    element.addEventListener('scroll', measure, { passive: true });

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            measure();
          })
        : null;

    const observeSizes = () => {
      if (!resizeObserver) {
        return;
      }

      resizeObserver.disconnect();
      resizeObserver.observe(element);
      Array.from(element.children).forEach(child => resizeObserver.observe(child));
    };

    observeSizes();

    const mutationObserver =
      typeof MutationObserver !== 'undefined'
        ? new MutationObserver(() => {
            observeSizes();
            measure();
          })
        : null;
    mutationObserver?.observe(element, { childList: true });

    return () => {
      element.removeEventListener('scroll', measure);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      if (requestRunning !== null) {
        window.cancelAnimationFrame(requestRunning);
      }
    };
  }, [ref, tick]);

  return overflown;
};
